/**
 * Formulario para crear/editar usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState, useEffect } from 'react';
import { useAllRoles, usePermissions } from '../../hooks';
import { userService } from '../../services';
import { Loading } from '../../components/UI/Loading';

const UserForm = ({ user, mode = 'create', onSubmit, onCancel }) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefono: '',
    role_id: '',
    permissions: [],
    is_active: true,
    // Campos específicos para vendedor
    comision_porcentaje: 0,
    tipo_pago: 'mensual',
    forma_pago: 'deposito',
    datos_bancarios: {
      banco: '',
      numero_cuenta: '',
      tipo_cuenta: 'ahorro',
      titular: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailValidating, setEmailValidating] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Hooks para datos
  const { roles, loading: rolesLoading } = useAllRoles();
  const {
    permissions,
    getPermissionsByModuleAndGroup,
    loading: permissionsLoading
  } = usePermissions();

  // Obtener permisos agrupados
  const permissionGroups = React.useMemo(() => {
    if (!getPermissionsByModuleAndGroup || !permissions || permissions.length === 0) {
      return {};
    }
    try {
      const grouped = getPermissionsByModuleAndGroup();
      return Object.fromEntries(
        Object.entries(grouped).map(([module, data]) => [module, data.groups || {}])
      );
    } catch (error) {
      console.error('Error grouping permissions:', error);
      return {};
    }
  }, [getPermissionsByModuleAndGroup, permissions]);

  // Inicializar formulario con datos del usuario si es edición
  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        email: user.email || '',
        password: '', // No cargar contraseña existente
        confirmPassword: '',
        telefono: user.telefono || '',
        role_id: user.role_id || '',
        permissions: user.permissions || [],
        is_active: user.is_active !== undefined ? user.is_active : true,
        // Campos de vendedor
        comision_porcentaje: user.vendedor_data?.comision_porcentaje || 0,
        tipo_pago: user.vendedor_data?.tipo_pago || 'mensual',
        forma_pago: user.vendedor_data?.forma_pago || 'deposito',
        datos_bancarios: {
          banco: user.vendedor_data?.datos_bancarios?.banco || '',
          numero_cuenta: user.vendedor_data?.datos_bancarios?.numero_cuenta || '',
          tipo_cuenta: user.vendedor_data?.datos_bancarios?.tipo_cuenta || 'ahorro',
          titular: user.vendedor_data?.datos_bancarios?.titular || ''
        }
      });

      // Buscar rol seleccionado
      if (user.role_id && roles.length > 0) {
        const role = roles.find(r => r.id === user.role_id);
        setSelectedRole(role);
      }
    }
  }, [user, mode, roles]);

  // Actualizar rol seleccionado cuando cambia el role_id
  useEffect(() => {
    if (formData.role_id && roles.length > 0) {
      const role = roles.find(r => r.id === formData.role_id);
      setSelectedRole(role);
    }
  }, [formData.role_id, roles]);

  // Manejador de cambios en campos
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('datos_bancarios.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        datos_bancarios: {
          ...prev.datos_bancarios,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar email único
  const validateEmail = async (email) => {
    if (!email || email === user?.email) return;

    setEmailValidating(true);
    try {
      const result = await userService.validateUniqueEmail(email, user?.id);
      if (!result.success) {
        setErrors(prev => ({
          ...prev,
          email: 'Este email ya está en uso'
        }));
      }
    } catch (error) {
      console.error('Error validating email:', error);
    } finally {
      setEmailValidating(false);
    }
  };

  // Manejar cambio de permisos
  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Seleccionar/deseleccionar todos los permisos de un módulo
  const toggleModulePermissions = (modulePermissions, allSelected) => {
    const permissionIds = modulePermissions.map(p => p.id);
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(id => !permissionIds.includes(id))
        : [...new Set([...prev.permissions, ...permissionIds])]
    }));
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validaciones básicas
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.apellido.trim()) {
      newErrors.apellido = 'El apellido es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (mode === 'create' && !formData.password) {
      newErrors.password = 'La contraseña es requerida';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'Debe seleccionar un rol';
    }

    // Validaciones específicas para vendedores
    if (selectedRole && selectedRole.name === 'vendedor') {
      if (formData.comision_porcentaje < 0 || formData.comision_porcentaje > 100) {
        newErrors.comision_porcentaje = 'La comisión debe estar entre 0 y 100%';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const result = await onSubmit(formData);
      
      if (result && !result.success) {
        // Manejar errores de validación del servidor
        if (result.validationErrors) {
          setErrors(result.validationErrors);
        } else {
          setErrors({ general: result.message });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ general: 'Error procesando formulario' });
    } finally {
      setSubmitting(false);
    }
  };

  // Mostrar loading mientras cargan los roles (requeridos)
  if (rolesLoading) {
    return <Loading message="Cargando formulario..." />;
  }

  // Si no hay roles después de cargar, mostrar error
  if (!roles || roles.length === 0) {
    return (
      <div className="alert alert-error">
        <i className="fas fa-exclamation-triangle"></i>
        No se pudieron cargar los roles del sistema. Por favor, intenta nuevamente.
      </div>
    );
  }

  const isVendedor = selectedRole && selectedRole.name === 'vendedor';

  return (
    <div className="user-form">
      <form onSubmit={handleSubmit}>
        {/* Error general */}
        {errors.general && (
          <div className="alert alert-error mb-4">
            <i className="fas fa-exclamation-triangle"></i>
            {errors.general}
          </div>
        )}

        <div className="form-sections">
          {/* Información Personal */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-user"></i>
              Información Personal
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="nombre" className="required">Nombre</label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                  placeholder="Ingrese el nombre"
                />
                {errors.nombre && (
                  <div className="invalid-feedback">{errors.nombre}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="apellido" className="required">Apellido</label>
                <input
                  type="text"
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  className={`form-control ${errors.apellido ? 'is-invalid' : ''}`}
                  placeholder="Ingrese el apellido"
                />
                {errors.apellido && (
                  <div className="invalid-feedback">{errors.apellido}</div>
                )}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="email" className="required">Email</label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={(e) => validateEmail(e.target.value)}
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    placeholder="usuario@ejemplo.com"
                  />
                  {emailValidating && (
                    <div className="input-icon">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                  )}
                </div>
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="telefono">Teléfono</label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Credenciales */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-key"></i>
              Credenciales
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="password" className={mode === 'create' ? 'required' : ''}>
                  {mode === 'create' ? 'Contraseña' : 'Nueva Contraseña (opcional)'}
                </label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Repetir contraseña"
                />
                {errors.confirmPassword && (
                  <div className="invalid-feedback">{errors.confirmPassword}</div>
                )}
              </div>
            </div>
          </div>

          {/* Rol y Estado */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-user-shield"></i>
              Rol y Estado
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="role_id" className="required">Rol</label>
                <select
                  id="role_id"
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleChange}
                  className={`form-control ${errors.role_id ? 'is-invalid' : ''}`}
                >
                  <option value="">Seleccionar rol</option>
                  {roles.map((role, index) => (
                    <option key={role.id || role.name || role || index} value={role.name || role.id || role}>
                      {role.display_name || role.name || role}
                    </option>
                  ))}
                </select>
                {errors.role_id && (
                  <div className="invalid-feedback">{errors.role_id}</div>
                )}
              </div>

              <div className="form-group">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="form-check-input"
                  />
                  <label htmlFor="is_active" className="form-check-label">
                    Usuario activo
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Configuración específica para vendedores */}
          {isVendedor && (
            <div className="form-section">
              <h3 className="section-title">
                <i className="fas fa-percentage"></i>
                Configuración de Vendedor
              </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="comision_porcentaje">Comisión (%)</label>
                  <input
                    type="number"
                    id="comision_porcentaje"
                    name="comision_porcentaje"
                    value={formData.comision_porcentaje}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className={`form-control ${errors.comision_porcentaje ? 'is-invalid' : ''}`}
                    placeholder="0.00"
                  />
                  {errors.comision_porcentaje && (
                    <div className="invalid-feedback">{errors.comision_porcentaje}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="tipo_pago">Frecuencia de Pago</label>
                  <select
                    id="tipo_pago"
                    name="tipo_pago"
                    value={formData.tipo_pago}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="semanal">Semanal</option>
                    <option value="quincenal">Quincenal</option>
                    <option value="mensual">Mensual</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="forma_pago">Forma de Pago</label>
                  <select
                    id="forma_pago"
                    name="forma_pago"
                    value={formData.forma_pago}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="deposito">Depósito Bancario</option>
                    <option value="cheque">Cheque</option>
                    <option value="zelle">Zelle</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              {/* Datos bancarios */}
              <div className="form-subsection">
                <h4>Datos Bancarios</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="datos_bancarios.banco">Banco</label>
                    <input
                      type="text"
                      id="datos_bancarios.banco"
                      name="datos_bancarios.banco"
                      value={formData.datos_bancarios.banco}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nombre del banco"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="datos_bancarios.tipo_cuenta">Tipo de Cuenta</label>
                    <select
                      id="datos_bancarios.tipo_cuenta"
                      name="datos_bancarios.tipo_cuenta"
                      value={formData.datos_bancarios.tipo_cuenta}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="ahorro">Ahorros</option>
                      <option value="corriente">Corriente</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="datos_bancarios.numero_cuenta">Número de Cuenta</label>
                    <input
                      type="text"
                      id="datos_bancarios.numero_cuenta"
                      name="datos_bancarios.numero_cuenta"
                      value={formData.datos_bancarios.numero_cuenta}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Número de cuenta"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="datos_bancarios.titular">Titular de la Cuenta</label>
                    <input
                      type="text"
                      id="datos_bancarios.titular"
                      name="datos_bancarios.titular"
                      value={formData.datos_bancarios.titular}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nombre del titular"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Permisos Adicionales */}
          {selectedRole && permissions && permissions.length > 0 && (
            <div className="form-section">
              <h3 className="section-title">
                <i className="fas fa-shield-alt"></i>
                Permisos Adicionales
                <small>Permisos específicos además de los incluidos en el rol "{selectedRole.display_name || selectedRole.name}"</small>
              </h3>

              <div className="permissions-table-container">
                <div className="permissions-table-header">
                  <div className="table-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: permissions.map(p => p.id)
                        }));
                      }}
                    >
                      <i className="fas fa-check-double"></i>
                      Seleccionar todos
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: []
                        }));
                      }}
                    >
                      <i className="fas fa-times"></i>
                      Deseleccionar todos
                    </button>
                  </div>
                </div>

                <div className="permissions-table-wrapper">
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={permissions.every(p => formData.permissions.includes(p.id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  permissions: permissions.map(p => p.id)
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  permissions: []
                                }));
                              }
                            }}
                            className="form-check-input"
                          />
                        </th>
                        <th>Permiso</th>
                        <th>Módulo</th>
                        <th>Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissions.map(permission => (
                        <tr
                          key={permission.id}
                          className={formData.permissions.includes(permission.id) ? 'selected' : ''}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                              className="form-check-input"
                            />
                          </td>
                          <td>
                            <strong>{permission.display_name || permission.name}</strong>
                            <br />
                            <small className="text-muted">{permission.name}</small>
                          </td>
                          <td>
                            <span className="module-badge">
                              {permission.module || 'General'}
                            </span>
                          </td>
                          <td>
                            <small>{permission.description || 'Sin descripción'}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="permissions-summary">
                  <i className="fas fa-info-circle"></i>
                  <span>
                    {formData.permissions.length} de {permissions.length} permisos seleccionados
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {mode === 'create' ? 'Creando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'create' ? 'fa-plus' : 'fa-save'}`}></i>
                {mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
